import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, Divider, Grid} from "@mui/material";
import {ProductivityTracker} from "../molecules/ProductivityTracker";
import WorkItemTable, { ViewType, caseContext } from "../organisms/workItemsTable";
import {ProductivityNumbers} from "../../queries/productivityTracker";
import {SnackbarContext} from "../../context/AlertsContext";
import {policiesContext, serviceLineMapper} from "../../utils/common";
import {fetchResolvedConfigurationValue} from "../../utils/api";
import theme from "../../theme/Theme";
import DefaultWrapper from "./DefaultWrapper";
import {WorkItemSettingsBtn} from "../atoms/buttons/WorkItemSettingsBtn";
import QueueSettingsDrawer from "../molecules/QueueSettingsDrawer/QueueSettingsDrawer";
import NavigationColumn from "../molecules/NavigationColumn";
import { AddViewPopup } from "../molecules/AddViewPopup";

interface IProps {
    client: any;
    widgetOptions: any;
    directoryType: string;
}

interface IProductivityTrackerProps {
    productivityNumbers: any;
    loading: boolean;
}

const ProductivityTrackerHeader = (props: IProductivityTrackerProps) => {
    const {productivityNumbers, loading} = props;

    return (
        <Grid item container xs={12}>
            {loading ? (
                <Grid container style={{gap: "1em", marginBottom: "20px"}}>
                    <Grid item style={{flex: 1}}>
                        <Box
                            sx={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "white",
                                boxShadow: "3",
                                border: "1",
                                borderRadius: "4px",
                            }}
                        >
                            <CircularProgress color="primary" size={20}/>
                        </Box>
                    </Grid>

                    <Grid item>
                        <Divider
                            orientation="vertical"
                            style={{
                                width: "2px",
                                backgroundColor: theme.palette.artisticStroke200.main
                            }}
                        />
                    </Grid>

                    <Grid item style={{flex: 1}}>
                        <Box
                            sx={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "white",
                                boxShadow: "3",
                                border: "1",
                                borderRadius: "4px",
                            }}
                        >
                            <CircularProgress color="primary" size={20}/>
                        </Box>
                    </Grid>
                </Grid>
            ) : (
                <ProductivityTracker
                    productivityNumbers={productivityNumbers}
                />
            )}
        </Grid>
    );
}

export default function Worklist(props: IProps) {
    const { client, widgetOptions, directoryType } = props;

    const {openSnackbar} = useContext(SnackbarContext);
    const { userContext, roles, policies } = React.useContext(policiesContext);

    const [showNewNav, setShowNewNav] = useState(false);
    const [productivityNumbers, setProductivityNumbers] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [refresh, setRefresh] = React.useState(false);

    const [queueSettingsOpen, setQueueSettingsOpen] = React.useState(false);

    const hasWorklistSettingsAccess = useMemo(() => {
        // @ts-ignore
        const resources = userContext?.activeContext?.resources || [];
        return resources.some((resource: any) => resource.resourceName === "worklist_settings");
    }, [userContext]);

    const [views, setViews] = useState<ViewType[]>([]);
    const [activeView, setActiveView] = useState<ViewType | undefined>();
    const [manageViewsAnchorEl, setManageViewsAnchorEl] = useState<HTMLElement | null>(null);
    // Use the external ref if provided, otherwise create a local one
    const tableRef = widgetOptions?.childRef || useRef<any>(null);

    useEffect(() => {
        const getProdTrackerData = async () => {
            setLoading(true);

            // Get the current date
            const currentDate = new Date();
            // Set the time to midnight
            currentDate.setHours(0, 0, 0, 0);
            // Convert to ISO string
            const midnightISOString = currentDate.toISOString();
            const variables = {
                currentDate: midnightISOString,
                service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name
            };
            await props?.client
                .query({
                    query: ProductivityNumbers,
                    variables: variables,
                    fetchPolicy: "no-cache",
                })
                .then((response: any) => {
                    const productivityNumbers = response.data.productivityNumbers;
                    setProductivityNumbers(productivityNumbers);
                })
                .catch((error: any) => {
                    openSnackbar("Failed to fetch Productivity Tracker statistics", "error");
                });

            setLoading(false);
        }

        if (directoryType) getProdTrackerData();
    }, [refresh, directoryType, client, openSnackbar, userContext]);

    useEffect(() => {
        // Fetch vertical-view-selector configuration
        const fetchNavConfig = async () => {
            if (userContext?.activeContext?.tenantId && userContext?.serviceLine?.name) {
                try {
                    const value = await fetchResolvedConfigurationValue(
                        "vertical-view-selector",
                        userContext.activeContext.tenantId,
                        userContext.serviceLine.name
                    );
                    setShowNewNav(value === "true");
                } catch (error) {
                    console.error("Failed to fetch vertical-view-selector config:", error);
                    setShowNewNav(false);
                }
            }
        };

        fetchNavConfig();
    }, [userContext?.activeContext?.tenantId, userContext?.serviceLine?.name]);

    const handleViewsLoaded = (loadedViews: ViewType[]) => {
        setViews(loadedViews);
    };

    const handleActiveViewChange = (view: ViewType) => {
        setActiveView(view);
    };

    const handleViewChange = (view: ViewType) => {
        if (tableRef.current?.handleViewChange) {
            tableRef.current.handleViewChange(view);
        }
    };

    const handleManageViewsClick = (event: React.MouseEvent<HTMLElement>) => {
        setManageViewsAnchorEl(event.currentTarget);
    };

    const handleCloseManageViews = () => {
        setManageViewsAnchorEl(null);
    };

    const handleRefreshViews = async () => {
        if (tableRef.current?.refreshViews) {
            await tableRef.current.refreshViews();
        }
    };

    if (!directoryType) return <></>;

    return (
        <DefaultWrapper>
            <Box sx={{
                display: "flex",
                height: "100%",
                width: "100%",
                margin: 0,
                marginLeft: 0,
                padding: 0,
                gap: 0,
                alignItems: "stretch"
            }}>
                {/* Navigation Sidebar - Only show if new nav is enabled */}
                {showNewNav && (
                    <NavigationColumn
                        mode={"worklist"}
                        views={views}
                        activeView={activeView}
                        onViewChange={handleViewChange}
                        roles={roles}
                        onManageViewsClick={handleManageViewsClick}
                    />
                )}

                {/* Main Content */}
                <Box sx={{
                    flex: 1,
                    overflow: "auto",
                    minWidth: 0,
                    width: "100%",
                    margin: 0,
                    padding: "20px"
                }}>
                    <WorkItemTable
                        DirectoryType={directoryType}
                        widgetHeader={widgetOptions?.prodTrackerActive
                            ? <ProductivityTrackerHeader loading={loading} productivityNumbers={productivityNumbers}/>
                            : null
                        }
                        widgetType={directoryType}
                        widgetTitle={"Worklist"}
                        widgetTitleActions={
                            hasWorklistSettingsAccess ? (
                                <WorkItemSettingsBtn
                                    onClick={() => setQueueSettingsOpen(true)}
                                    alt={"queue settings"}
                                />
                            ) : null
                        }
                        client={client}
                        handleRowClick={(workitem, data) => {
                            widgetOptions?.onWorkItemSelected(workitem, {
                                ...data,
                                isCodingQuality: false,
                            });
                        }}
                        onTableRefresh={() => setRefresh((prev) => !prev)}
                        childRef={tableRef}
                        handleViewWorkitemData={(workitem) => widgetOptions?.viewWorkitemData(workitem)}
                        hideTabNavigation={showNewNav}
                        onViewsLoaded={handleViewsLoaded}
                        onActiveViewChange={handleActiveViewChange}
                    />
                </Box>
            </Box>

            {/* Manage Views Popup - Only show for new nav */}
            {showNewNav && tableRef.current && (
                <caseContext.Provider
                    value={{
                        directoryType: directoryType,
                        columnOptions: tableRef.current?.getColumnOptions?.() || [],
                        detailGridOptions: tableRef.current?.getDetailGridOptions?.() || [],
                        optionsDict: tableRef.current?.getOptionsDict?.() || [],
                        setOptionsDict: tableRef.current?.setOptionsDict
                    }}
                >
                    <AddViewPopup
                        anchorEl={manageViewsAnchorEl}
                        handleClose={handleCloseManageViews}
                        client={client}
                        handleSaveView={tableRef.current?.handleSaveView || (() => {})}
                        options={tableRef.current?.getColumnOptions?.() || []}
                        onHide={() => {}}
                        onSelect={() => {}}
                        existingView={activeView}
                        views={views}
                        roles={roles}
                        allCoders={tableRef.current?.getAllCoders?.() || []}
                        allGroups={tableRef.current?.getAllGroups?.() || []}
                        queues={tableRef.current?.getQueues?.() || []}
                        directoryType={directoryType}
                        widgetType={directoryType}
                        refreshViews={handleRefreshViews}
                        filters={[]}
                        setFilters={() => {}}
                    />
                </caseContext.Provider>
            )}

            {hasWorklistSettingsAccess && (
                <QueueSettingsDrawer
                    isOpen={queueSettingsOpen}
                    onClose={() => setQueueSettingsOpen(false)}
                    client={props.client}
                    enableQueueViews={showNewNav}
                />
            )}
        </DefaultWrapper>
    );
}
